import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Check,
  Download,
  FileCheck2,
  FileText,
  Hash,
  Mail,
  Mic,
  MoreHorizontal,
  QrCode,
  Send,
  ShieldCheck
} from "lucide-react";
import { apiUrl, responseError } from "../app/api";
import { FEATURE_FLAGS } from "../app/feature-flags";
import { previewPageGroups } from "../app/preview-pagination";
import { diffTranscript } from "../app/transcript-diff";
import type {
  ExportOptions,
  MatterDraft,
  TranscriptRow,
  TranscriptSession
} from "../app/ui-types";

type ExportPanelProps = {
  rows: TranscriptRow[];
  matter: MatterDraft;
  session: TranscriptSession | null;
  reviewerName: string;
  onStartNewRecording?: () => void;
  matterFinalisationId?: string;
  sandbox?: boolean;
  onCreateAccount?: () => void;
};

type ExportState = "idle" | "loading" | "success" | "error";

const pageTime = (milliseconds: number) => {
  const totalSeconds = Math.floor(milliseconds / 1000);
  return `${String(Math.floor(totalSeconds / 60)).padStart(2, "0")}:${String(
    totalSeconds % 60
  ).padStart(2, "0")}`;
};

const displayDate = (date: string) => {
  const value = new Date(`${date}T00:00:00`);
  if (Number.isNaN(value.valueOf())) return "Date not supplied";
  return new Intl.DateTimeFormat("en-AU", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  }).format(value);
};

export function ExportPanel({
  rows,
  matter,
  session,
  reviewerName,
  onStartNewRecording,
  matterFinalisationId,
  sandbox = false,
  onCreateAccount
}: ExportPanelProps) {
  const [options, setOptions] = useState<ExportOptions>({
    layout: FEATURE_FLAGS.minuteView ? "concise-minute" : "jurisdiction",
    format: "clean",
    includeTimestamps: true,
    includeExhibitIndex: true,
    includeWitnessIndex: true,
    jurisdictionPack: matter.jurisdictionPack
  });
  const [exportState, setExportState] = useState<ExportState>("idle");
  const [exportId, setExportId] = useState("");
  const [exportMessage, setExportMessage] = useState("");
  const [exportMeta, setExportMeta] = useState({
    sha256: "",
    verificationUrl: "",
    versionId: session?.versionId ?? "Not frozen"
  });
  const [recipient, setRecipient] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [attachmentPassword, setAttachmentPassword] = useState("");
  const [moreDeliveryOptions, setMoreDeliveryOptions] = useState(false);
  const [deliveryState, setDeliveryState] = useState<ExportState>("idle");
  const [deliveryMessage, setDeliveryMessage] = useState("");
  const pageGroups = useMemo(
    () => previewPageGroups(rows, matter.jurisdictionPack),
    [rows, matter.jurisdictionPack]
  );

  useEffect(() => {
    setExportId("");
    setExportState("idle");
    setExportMessage("");
    setDeliveryState("idle");
    setDeliveryMessage("");
    setExportMeta({
      sha256: "",
      verificationUrl: "",
      versionId: session?.versionId ?? "Not frozen"
    });
  }, [options, session?.versionId]);

  const downloadExport = async (id: string) => {
    const pdfResponse = await fetch(apiUrl(`/api/exports/${id}/pdf`));
    if (!pdfResponse.ok) {
      throw await responseError(pdfResponse, "The verified PDF could not be downloaded.");
    }
    const blob = await pdfResponse.blob();
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = `${matter.reference || "transcript"}-${options.format}.pdf`;
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const createExport = async () => {
    if (!session || session.state !== "frozen") {
      setExportState("error");
      setExportMessage("Finish a recording, review it, and freeze its version before export.");
      return;
    }
    setExportState("loading");
    setExportMessage("");
    try {
      const response = await fetch(apiUrl("/api/exports"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          transcriptVersionId: session.versionId,
          ...(matterFinalisationId ? { matterFinalisationId } : {}),
          view: options.format,
          layout: options.layout,
          reviewerName,
          includeTimestamps: options.includeTimestamps,
          includeExhibitIndex: options.includeExhibitIndex,
          includeWitnessIndex: options.includeWitnessIndex
        })
      });
      if (!response.ok) {
        throw await responseError(response, "The verified PDF could not be created.");
      }
      const result = (await response.json()) as {
        id: string;
        downloadUrl: string;
        sha256: string;
        verificationUrl: string;
        transcriptVersionId: string;
      };
      await downloadExport(result.id);
      setExportId(result.id);
      setExportMeta({
        sha256: result.sha256,
        verificationUrl: result.verificationUrl,
        versionId: result.transcriptVersionId
      });
      setExportState("success");
      setExportMessage("Verified PDF created and downloaded.");
    } catch (cause) {
      setExportState("error");
      setExportMessage(
        cause instanceof Error ? cause.message : "The verified PDF could not be created."
      );
    }
  };

  const sendExport = async (
    event: FormEvent,
    mode: "secure-link" | "encrypted-attachment" = "secure-link"
  ) => {
    event.preventDefault();
    if (!recipient.includes("@")) {
      setDeliveryState("error");
      setDeliveryMessage("That address is incomplete. Enter a valid recipient address.");
      return;
    }
    if (mode === "secure-link" && !recipientName.trim()) {
      setDeliveryState("error");
      setDeliveryMessage("Enter the recipient’s name so comments and access records are attributable.");
      return;
    }
    if (!exportId) {
      setDeliveryState("error");
      setDeliveryMessage("Create the verified PDF before preparing delivery.");
      return;
    }
    if (mode === "encrypted-attachment" && attachmentPassword.length < 12) {
      setDeliveryState("error");
      setDeliveryMessage("Use at least 12 characters for the attachment password.");
      return;
    }
    setDeliveryState("loading");
    setDeliveryMessage("");
    try {
      const response = await fetch(apiUrl(
        mode === "secure-link"
          ? `/api/exports/${encodeURIComponent(exportId)}/review-shares`
          : "/api/deliveries/email"
      ), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          mode === "secure-link"
            ? { recipientName, recipientEmail: recipient }
            : {
                exportId,
                recipient,
                method: "encrypted-pdf",
                expiresInHours: 72,
                password: attachmentPassword
              }
        )
      });
      if (!response.ok) {
        throw await responseError(response, "Delivery could not be prepared.");
      }
      setDeliveryState("success");
      setDeliveryMessage(
        mode === "secure-link"
          ? `Reviewed transcript sent to ${recipientName}. Access is email-bound, revocable, and expires in 72 hours.`
          : `Encrypted attachment prepared for ${recipient}. Send its password separately.`
      );
    } catch (cause) {
      setDeliveryState("error");
      setDeliveryMessage(
        cause instanceof Error
          ? cause.message
          : "Delivery was not queued. Export remains available."
      );
    }
  };

  return (
    <div className="export-workbench">
      <header className="page-heading">
        <div>
          <p className="overline">{matter.reference}</p>
          <h1>Finalise the transcript.</h1>
          <p>Create the reviewed PDF, then download it or send a secure link.</p>
        </div>
        <span
          className={
            session?.state === "frozen"
              ? "status-mark status-mark--success"
              : "status-mark"
          }
        >
          <FileCheck2 size={14} aria-hidden="true" />
          {sandbox
            ? "Sample transcript"
            : session?.state === "frozen"
            ? `Version ${session.versionNumber} frozen`
            : "Frozen version required"}
        </span>
      </header>

      <div className="export-layout">
        <aside className="export-controls">
          <section aria-labelledby="export-format-title">
            <div className="section-title section-title--compact">
              <FileText size={17} aria-hidden="true" />
              <div>
                <h2 id="export-format-title">PDF format</h2>
                <p>Confidence colour never appears in clean output.</p>
              </div>
            </div>
            <div className="choice-list">
              <label className="choice-row">
                <input
                  type="radio"
                  name="layout"
                  checked={options.layout === "concise-minute"}
                  onChange={() =>
                    setOptions((current) => ({ ...current, layout: "concise-minute" }))
                  }
                />
                <span>
                  <strong>Concise minute blocks</strong>
                  <small>One numbered block per recorded minute, with speaker segments.</small>
                </span>
              </label>
              <label className="choice-row">
                <input
                  type="radio"
                  name="layout"
                  checked={options.layout === "jurisdiction"}
                  onChange={() =>
                    setOptions((current) => ({ ...current, layout: "jurisdiction" }))
                  }
                />
                <span>
                  <strong>Jurisdiction layout</strong>
                  <small>Existing formal pagination and physical-line rules.</small>
                </span>
              </label>
            </div>
            <div className="choice-list">
              <label className="choice-row">
                <input
                  type="radio"
                  name="format"
                  checked={options.format === "clean"}
                  onChange={() => setOptions((current) => ({ ...current, format: "clean" }))}
                />
                <span>
                  <strong>Clean transcript</strong>
                  <small>Reviewed text with no change marks.</small>
                </span>
              </label>
              <label className="choice-row">
                <input
                  type="radio"
                  name="format"
                  checked={options.format === "redline"}
                  onChange={() => setOptions((current) => ({ ...current, format: "redline" }))}
                />
                <span>
                  <strong>Redline transcript</strong>
                  <small>Additions underlined; deletions struck through.</small>
                </span>
              </label>
            </div>
            <label className="field">
              <span>Jurisdiction pack</span>
              <select
                value={options.jurisdictionPack}
                disabled
              >
                <option value="AU-NSW">Australia · NSW</option>
                <option value="GLOBAL-BASE">Global base</option>
                <option value="US-FED-DEPOSITION">United States · Federal deposition</option>
              </select>
              <small>Layout rules are versioned with the export.</small>
            </label>
            <div className="check-list">
              <label>
                <input
                  type="checkbox"
                  checked={options.includeTimestamps}
                  onChange={(event) =>
                    setOptions((current) => ({
                      ...current,
                      includeTimestamps: event.target.checked
                    }))
                  }
                />
                Timestamps
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={options.includeExhibitIndex}
                  onChange={(event) =>
                    setOptions((current) => ({
                      ...current,
                      includeExhibitIndex: event.target.checked
                    }))
                  }
                />
                Exhibit index
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={options.includeWitnessIndex}
                  onChange={(event) =>
                    setOptions((current) => ({
                      ...current,
                      includeWitnessIndex: event.target.checked
                    }))
                  }
                />
                Witness index
              </label>
            </div>
          </section>

          {!sandbox && (
            <>
              <section aria-labelledby="create-export-title">
                <div className="section-title section-title--compact">
                  <ShieldCheck size={17} aria-hidden="true" />
                  <div>
                    <h2 id="create-export-title">Create verified PDF</h2>
                    <p>Freeze the reviewed version and record its integrity hash.</p>
                  </div>
                </div>
              <button
                className="button button--primary button--wide"
                type="button"
                onClick={createExport}
                aria-busy={exportState === "loading"}
                disabled={exportState === "loading" || session?.state !== "frozen"}
                title={
                  session?.state === "frozen"
                    ? undefined
                    : "Finish review and freeze a version before export."
                }
              >
                <Download size={16} aria-hidden="true" />
                {exportState === "loading"
                  ? "Building PDF…"
                  : exportState === "success"
                    ? "PDF created"
                    : "Create verified PDF"}
              </button>
                {exportState === "error" && (
                  <p className="inline-error">{exportMessage}</p>
                )}
                {exportState === "success" && (
                  <p className="inline-success">
                    <Check size={15} aria-hidden="true" />
                    {exportMessage}
                  </p>
                )}
              </section>

              <form
                className="export-action-rail"
                onSubmit={(event) => sendExport(event, "secure-link")}
              >
                <label className="field">
                  <span>Recipient name</span>
                  <input
                    value={recipientName}
                    onChange={(event) => {
                      setRecipientName(event.target.value);
                      setDeliveryState("idle");
                    }}
                    autoComplete="name"
                    placeholder="Alex Morgan"
                  />
                </label>
                <label className="field">
                  <span>Recipient address</span>
                  <input
                    type="email"
                    value={recipient}
                    onChange={(event) => {
                      setRecipient(event.target.value);
                      setDeliveryState("idle");
                    }}
                    placeholder="reviewer@firm.example"
                    aria-invalid={deliveryState === "error"}
                  />
                </label>
                <button
                  className="button button--primary button--wide"
                  type="submit"
                  aria-busy={deliveryState === "loading"}
                  disabled={deliveryState === "loading" || !exportId}
                >
                  <Send size={16} aria-hidden="true" />
                  {deliveryState === "loading"
                    ? "Sending secure review link…"
                    : "Send secure review link"}
                </button>
                <button
                  className="button button--quiet button--wide"
                  type="button"
                  disabled={!exportId}
                  onClick={async () => {
                    try {
                      await downloadExport(exportId);
                    } catch (cause) {
                      setExportState("error");
                      setExportMessage(
                        cause instanceof Error
                          ? cause.message
                          : "The verified PDF could not be downloaded."
                      );
                    }
                  }}
                >
                  <Download size={16} aria-hidden="true" />
                  Download PDF
                </button>
                <button
                  className="button button--text button--wide"
                  type="button"
                  aria-expanded={moreDeliveryOptions}
                  onClick={() => setMoreDeliveryOptions((current) => !current)}
                >
                  <MoreHorizontal size={16} aria-hidden="true" />
                  More delivery options
                </button>
              </form>

              <section aria-labelledby="delivery-title">
                <div className="section-title section-title--compact">
                  <Mail size={17} aria-hidden="true" />
                  <div>
                    <h2 id="delivery-title">Delivery and audit</h2>
                    <p>Secure-link access expires in 72 hours and is logged.</p>
                  </div>
                </div>
                {moreDeliveryOptions && (
                  <form
                    className="form-stack"
                    onSubmit={(event) => sendExport(event, "encrypted-attachment")}
                  >
                    <label className="field">
                      <span>Attachment password</span>
                      <input
                        type="password"
                        minLength={12}
                        value={attachmentPassword}
                        onChange={(event) => {
                          setAttachmentPassword(event.target.value);
                          setDeliveryState("idle");
                        }}
                        autoComplete="new-password"
                        placeholder="At least 12 characters"
                      />
                      <small>
                        Send it separately. Dicta never emails the password with the file.
                      </small>
                    </label>
                    <button
                      className="button button--quiet button--wide"
                      type="submit"
                      disabled={deliveryState === "loading" || !exportId}
                    >
                      <Mail size={16} aria-hidden="true" />
                      Send encrypted attachment
                    </button>
                  </form>
                )}
                {deliveryMessage && (
                  <p
                    className={
                      deliveryState === "success" ? "inline-success" : "inline-error"
                    }
                    role="status"
                  >
                    {deliveryState === "success" && (
                      <Check size={15} aria-hidden="true" />
                    )}
                    {deliveryMessage}
                  </p>
                )}
              </section>
            </>
          )}

          {sandbox && (
            <section aria-labelledby="sample-export-title">
              <div className="section-title section-title--compact">
                <ShieldCheck size={17} aria-hidden="true" />
                <div>
                  <h2 id="sample-export-title">Ready to issue it?</h2>
                  <p>Create an account to verify, download, and deliver transcripts.</p>
                </div>
              </div>
              <button
                className="button button--primary button--wide"
                type="button"
                onClick={onCreateAccount}
              >
                Create a free account to issue this transcript
              </button>
            </section>
          )}

          {onStartNewRecording && (
            <section aria-labelledby="export-next-title">
              <div className="section-title section-title--compact">
                <Mic size={17} aria-hidden="true" />
                <div>
                  <h2 id="export-next-title">Finished with this one?</h2>
                  <p>This transcription stays on the record. Start the next.</p>
                </div>
              </div>
              <button
                className="button button--quiet"
                type="button"
                onClick={onStartNewRecording}
              >
                <Mic size={15} aria-hidden="true" />
                New transcription
              </button>
            </section>
          )}
        </aside>

        <main className="pdf-preview-wrap" aria-label="PDF preview">
          <div className="pdf-toolbar">
            <span>
              <FileText size={15} aria-hidden="true" />
              {matter.reference}-{options.format}.pdf
            </span>
            <span>Layout preview</span>
          </div>
          {pageGroups.map((group, index) => (
            <article className="pdf-page" key={group.page}>
              {index === 0 && (
                <>
                  <header className="pdf-page__header">
                    <span>IN THE MATTER OF</span>
                    <strong>{matter.title.toUpperCase()}</strong>
                    <span>TRANSCRIPTION</span>
                  </header>
                  <dl className="pdf-metadata">
                    <div>
                      <dt>Date</dt>
                      <dd>{displayDate(matter.date)}</dd>
                    </div>
                    <div>
                      <dt>Venue</dt>
                      <dd>{matter.venue}</dd>
                    </div>
                    <div>
                      <dt>Time zone</dt>
                      <dd>{matter.timezone}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>Reviewed — Not Certified</dd>
                    </div>
                  </dl>
                </>
              )}
              <div className="pdf-transcript">
                {group.rows.map((row) => (
                  <div className="pdf-line" key={row.id}>
                    <span>{row.line}</span>
                    {options.includeTimestamps && <time>{pageTime(row.startMs)}</time>}
                    <strong>{row.role}</strong>
                    <p>
                      {options.format === "redline"
                        ? diffTranscript(row.baseline, row.text).map((token, index) => {
                            if (token.kind === "added") return <ins key={index}>{token.value}</ins>;
                            if (token.kind === "removed") return <del key={index}>{token.value}</del>;
                            return <span key={index}>{token.value}</span>;
                          })
                        : row.text}
                    </p>
                  </div>
                ))}
              </div>
              {index === pageGroups.length - 1 && (
                <footer className="pdf-stamp">
                  <div className="verified-stamp">
                    <ShieldCheck size={18} aria-hidden="true" />
                    <span>
                      <strong>Dicta Verified Export</strong>
                      <small>Reviewed — Not Certified</small>
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>
                        <Hash size={12} aria-hidden="true" /> Version
                      </dt>
                      <dd>{exportMeta.versionId}</dd>
                    </div>
                    <div>
                      <dt>SHA-256</dt>
                      <dd>
                        {exportMeta.sha256
                          ? `${exportMeta.sha256.slice(0, 20)}…`
                          : "Created with verified export"}
                      </dd>
                    </div>
                    <div>
                      <dt>
                        <QrCode size={12} aria-hidden="true" /> Verification
                      </dt>
                      <dd>{exportMeta.verificationUrl || "Created with verified export"}</dd>
                    </div>
                  </dl>
                </footer>
              )}
              <span className="pdf-page-number">
                Page {group.page} of {pageGroups.length}
              </span>
            </article>
          ))}

        </main>
      </div>
    </div>
  );
}
